import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import Step1 from 'components/join/Step1';
import Step2 from 'components/join/Step2';
import Step3 from 'components/join/Step3';
import DefaultButton from 'components/button/DefaultButton';
import { useSetAtom } from 'jotai';
import modalInformationAtom from 'atom/modalInformationAtom';
import { useMutation } from '@tanstack/react-query';
import { restfetcher } from 'queryClient';
import { useNavigate } from 'react-router-dom';
import * as S from '../components/join/Step1';

import { ReactComponent as Logo } from 'asserts/logo/logo_exbody.svg';
import { FieldErrors, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { INITIAL_JOIN_INFO, JOIN_STEP } from 'consts/join';
import { scrollToErrorField } from 'utils/common';
import usePreventPage from 'hooks/usePreventPage';

type JoinInfoTypeWithoutSearch = Omit<
  JoinTypeOnlyUsedFront,
  | 'code'
  | 'searchCompanyKeyword'
  | 'passwordCheck'
  | 'contractname'
  | 'contracturl'
  | 'usrphoneregioncodeidx'
  | 'nationcodeidx'
  | 'parentusrname'
  | 'bizFile'
  | 'terms'
>;

type ExtendedJoinInfoType = JoinInfoTypeWithoutSearch & {
  term1: number;
  term2: number;
  term3: number;
  term4: number;
};

const Join = () => {
  const navigate = useNavigate();

  const setModalInformation = useSetAtom(modalInformationAtom);

  const [hasReset, setHasReset] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [step1, setStep1] = useState(false);
  const [terms, setTerms] = useState<TermsTypes>({
    term1: false,
    term2: false,
    term3: false,
    term4: false
  });
  const [emailState, setEmailState] = useState<InputEmail>({
    emailId: '',
    selectedEmailAddress: '',
    selectedEmailAddressIdx: '',
    directInputEmail: '',
    error: { text: '', type: '' }
  });
  const [businessNumber, setBusinessNumber] = useState({
    businessNumber1: '',
    businessNumber2: '',
    businessNumber3: ''
  });
  const [bizFileData, setBizFileData] = useState<FileType | undefined>(undefined);
  const [isLoding, setIsLoading] = useState(false);

  const isJoinButton = selectedTab === 2 ? '가입' : '다음';

  const defaultValues = useMemo(() => INITIAL_JOIN_INFO, []);

  const formMethods = useForm<JoinTypeOnlyUsedFront>({
    mode: 'onChange',
    defaultValues,
    shouldFocusError: true,
    shouldUnregister: false
  });

  const { watch, handleSubmit } = formMethods;

  const usrIdx = watch('usridx');
  const isJoinUser = usrIdx !== 0;

  const handleChangeTerms = (name: string, value: boolean) => {
    let newTerms = {
      term1: value,
      term2: value,
      term3: value,
      term4: value
    };
    if (name === 'all') {
      setTerms(newTerms);
    } else {
      setTerms({ ...terms, [name]: value });
    }
  };

  const handleClickPreviousStep = () => {
    if (selectedTab !== 3) {
      if (selectedTab > 0) {
        setSelectedTab(selectedTab - 1);
      }
    } else if (selectedTab === 3) {
      setSelectedTab(selectedTab - 1);
    }
  };

  const handleClickNextStep = () => {
    switch (selectedTab) {
      case 0:
        if (!step1)
          return setModalInformation({
            type: 'NOSENDEMAIL_CODE'
          });
        break;
      case 1:
        if (!terms.term1 || !terms.term2 || !terms.term3) {
          setModalInformation({
            type: 'WARNING',
            message: '[필수] 약관에 동의해주세요.'
          });
          return;
        }
        break;
      case 2:
        setModalInformation({
          type: 'WARNING_AND_NEXT',
          message: '가입 하시겠습니까?',
          handleClickRightBtn: handleSubmit(onSubmit, onInvalid)
        });
        break;
      default:
        break;
    }

    if (selectedTab !== 2) {
      if (selectedTab < JOIN_STEP.length - 1) {
        setSelectedTab(selectedTab + 1);
      }
    }
  };

  const onInvalid = (errors: FieldErrors<JoinTypeOnlyUsedFront>) => {
    console.log(errors);
    scrollToErrorField(errors);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const requestJoin = useMutation({
    mutationFn: (formData: FormData) =>
      restfetcher({
        method: 'POST',
        path: 'biz/auth/join',
        params: formData
      }),
    onSuccess: (data: any) => {
      if (data?.result === 'success') {
        setModalInformation({
          type: 'CONFIRM',
          powerOK: true,
          message: '회원가입이 완료되었습니다. 서비스 이용을 위해서 로그인해주세요.',
          handleClickRightBtn: () => navigate('/login')
        });
      } else if (data?.result === 'failed' && data?.exception.includes('phoneno is exist')) {
        setModalInformation({
          type: 'WARNING',
          message: '이미 등록된 휴대폰 번호입니다.'
        });
      } else {
        setModalInformation({
          type: 'WARNING',
          message: '실패했습니다.'
        });
      }
    },
    onMutate: () => {
      setIsLoading(false);
    }
  });

  const onSubmit: SubmitHandler<JoinTypeOnlyUsedFront> = (params) => {
    const fileForm = new FormData();

    const {
      contractname,
      contracturl,
      passwordCheck,
      usrphoneregioncodeidx,
      nationcodeidx,
      presignupidx,
      isContractAgreed,
      bizFile,
      bizlicensename,
      usrphoneno,
      usrphoneregioncode,
      countryphoneno,
      preregcodeidx,
      ...updatedParams
    } = params;

    const combined =
      businessNumber.businessNumber1 &&
      businessNumber.businessNumber2 &&
      businessNumber.businessNumber3
        ? `${businessNumber.businessNumber1}-${businessNumber.businessNumber2}-${businessNumber.businessNumber3}`
        : '';

    const addUsrIdxParams: ExtendedJoinInfoType = {
      ...updatedParams,
      term1: terms?.term1 ? 1 : 0,
      term2: terms?.term2 ? 1 : 0,
      term3: terms?.term3 ? 1 : 0,
      term4: terms?.term4 ? 1 : 0,
      bizlicensename: bizlicensename,
      presignupidx: presignupidx,
      usridx: usrIdx || 0,
      bizlicensenum: combined,
      usrphoneno: usrphoneno,
      usrphoneregioncode: usrphoneno ? 'KR' : '',
      countryphoneno: usrphoneno ? '+82' : '',
      isContractAgreed: contracturl ? isContractAgreed : 0,
      preregcodeidx: preregcodeidx,
    };

    fileForm.append('param', JSON.stringify(addUsrIdxParams));
    fileForm.append('bizlicenseurl', bizFile);

    /*if (!bizFileData?.name) {
      setModalInformation({
        type: 'WARNING',
        message: '등록증을 첨부해주세요.'
      });
    } else if (!combined) {
      setModalInformation({
        type: 'WARNING',
        message: '등록증 번호를 입력해주세요.'
      });
    } else {
      requestJoin.mutate(fileForm);
      setIsLoading(true);
    }*/ //warren

    requestJoin.mutate(fileForm);
    setIsLoading(true);
  };

  const joinStepContents = [
    {
      component: (
        <Step1 setStep1={setStep1} emailState={emailState} setEmailState={setEmailState} />
      ),
      title: '비즈니스 인증',
      text: '이메일 및 사업자 번호 인증을 진행합니다.'
    },
    {
      component: <Step2 terms={terms} onChange={handleChangeTerms} />,
      title: '약관동의',
      text: `엑스바디 비즈멤버십 가입을 위해서\n아래 이용약관 및 개인정보 수집 및 이용 동의에 대한 안내를 읽고 동의해 주세요.`
    },
    {
      component: (
        <Step3
          hasReset={hasReset}
          setHasReset={setHasReset}
          isJoinUser={isJoinUser}
          bizFileData={bizFileData}
          setBizFileData={setBizFileData}
          businessNumber={businessNumber}
          setBusinessNumber={setBusinessNumber}
        />
      ),
      title: '정보 입력',
      text: '* 회원 정보를 확인해주세요.'
    }
  ];

  const currentStep = joinStepContents[selectedTab] || {
    component: null,
    title: null,
    text: null
  };

  const { component: renderedComponent, title, text } = currentStep;

  const handleClickGoToMain = () => {
    navigate('/');
  };

  // const handleTabClick = (index: number) => {
  //   setSelectedTab(index);
  // };

  usePreventPage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedTab]);

  return (
    <>
      <Wrapper>
        <InnerWrapper>
          <Logo fill="#05054C" onClick={handleClickGoToMain} style={{ cursor: 'pointer' }} />
          <div className="layout">
            <TabsWrapper>
              {JOIN_STEP.map((item, index) => (
                <TabItem
                  key={item}
                  $isSelected={selectedTab === index}
                  // onClick={() => handleTabClick(index)}
                >
                  {item}
                </TabItem>
              ))}
            </TabsWrapper>
            <h2>
              <strong>STEP {selectedTab + 1}</strong> {title}
            </h2>
          </div>
          <pre>{text}</pre>
        </InnerWrapper>
        <S.Line />
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} onKeyDown={handleKeyDown}>
          <FormProvider {...formMethods}>{renderedComponent}</FormProvider>
          <ButtonWrapper>
            {selectedTab > 0 && (
              <DefaultButton onClick={handleClickPreviousStep} text="이전" size="middle" />
            )}
            <DefaultButton
              onClick={handleClickNextStep}
              text={isJoinButton}
              size="middle"
              type="primary"
              disabled={isLoding}
            />
          </ButtonWrapper>
          <input type="submit" hidden />
        </form>
      </Wrapper>
    </>
  );
};

export default Join;

const Wrapper = styled.div`
  width: 1440px;
  margin: 0 auto;
  padding: 120px 275px;

  h2 {
    color: ${color.navyLight};
    ${font.d1_light};
    strong {
      font-weight: 700;
    }
  }

  & div.layout {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;

  pre {
    ${font.h3_Regular};
  }
`;

const TabsWrapper = styled.ul`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const TabItem = styled.li<{
  $isSelected: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: ${(props) => (props.$isSelected ? '64px' : '24px')};
  height: 24px;
  cursor: initial;
  font-weight: 500;
  line-height: 20px;
  color: ${(props) => (props.$isSelected ? `${color.white2}` : `${color.gray400}`)};
  background-color: ${(props) => (props.$isSelected ? `${color.navyLight}` : `${color.gray200}`)};
  border-radius: ${(props) => (props.$isSelected ? '24px' : '36px')};
  ${font.caption1_medium};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  .ant-btn {
    width: 160px;
  }
`;
